/**
 * Companion — Welle 8c Phase A
 *
 * Zweite Berater-Session auf dem Terminal-Server (kein A/V). Sie meldet sich mit
 * derselben Berater-Anmeldung an, fragt per /api/calls/companion/attach den aktiven
 * Call ab (Polling, folgt der Berater-Session über Call-Grenzen hinweg) und baut
 * über useCompanionRtc eine eigene P2P-Verbindung zur Stele auf — Steuerkanal nur,
 * kein A/V. Der A/V-Pfad zwischen A/V-Berater und Stele bleibt unberührt.
 *
 * Phase A weist nur Attach + Verbindung + offenen Steuerkanal nach. Screen-Share
 * (Phase B) und Scan/Signierung (Phase C) bauen darauf auf.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, getDisplayName, clearToken } from '@/lib/auth';
import { useCompanionRtc } from '@/hooks/useCompanionRtc';
import { saveToDir, saveWithDialog, getDirName, getHandles, getAccessStates, requestAccessAll, requestAccessFor, type TargetKind } from '@/lib/fileTargets';
import { writeRecordingToDir, getRecordingHandle, getRecordingDirName, requestRecordingAccess, pickRecordingDir, clearRecordingDir, getRecordingAccess, dirPickerSupported } from '@/lib/recordingDelivery';
import { fetchRecordingPolicy, enabledTopics, DEFAULT_POLICY, type RecordingPolicy } from '@/lib/recordingPolicy';
import { useRemoteControl } from '@/hooks/useRemoteControl';
import { useCompanionPip, type CompanionPipState, type CompanionPipHandlers } from '@/hooks/useCompanionPip';
import { IncomingCallCard } from '@/components/call/IncomingCallCard';
import { CallControlBar } from '@/components/call/CallControlBar';
import { CameraControls } from '@/components/call/CameraControls';
import { RecordingConsentModal } from '@/components/call/RecordingConsentModal';
import { barBtn } from '@/components/call/controlBarStyles';
import { JoinRequestPrompt } from '@/components/JoinRequestPrompt';
import { TransferDrawer } from '@/components/CallDrawers';
import { ChatDrawer } from '@/components/ChatDrawer';
import { useUIStore } from '@/state/store';
import { Roster } from '@/components/Roster';
import { StatusSwitcher } from '@/components/Header';
import { SignInbox } from '@/components/SignInbox';
import { FolderTargetsConfig } from '@/components/FolderTargetsConfig';
import { buildEcProtocolPdf } from '@/lib/ecPdf';
import type { NfcReadPayload } from '@/state/store';

type AttachState = 'searching' | 'attached' | 'error';

const STATUS_LABEL: Record<string, string> = {
  idle: 'Bereit',
  connecting: 'Verbinde…',
  'waiting-peer': 'Warte auf Stele…',
  negotiating: 'Aushandlung…',
  connected: 'Verbunden',
  error: 'Fehler',
  ended: 'Beendet',
};

export function Companion() {
  const [callId, setCallId] = useState<string | null>(null);
  const [rtcToken, setRtcToken] = useState<string | null>(null);
  const [e2ee, setE2ee] = useState(false);
  const [attach, setAttach] = useState<AttachState>('searching');
  const callIdRef = useRef<string | null>(null);

  // Attach-Polling: aktiven Call der angemeldeten Berater-Session suchen.
  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      try {
        const res = await apiFetch('/api/calls/companion/attach', { method: 'POST' });
        if (!res.ok) { if (!stopped) setAttach('error'); return; }
        const data = await res.json();
        if (stopped) return;
        if (data?.callId && data?.rtcToken) {
          if (data.callId !== callIdRef.current) {
            callIdRef.current = data.callId;
            setCallId(data.callId);
            setE2ee(Boolean(data.e2ee));
            setRtcToken(data.rtcToken); // neuer Token → useCompanionRtc verbindet (neu)
          }
          setAttach('attached');
        } else {
          if (callIdRef.current !== null) {
            callIdRef.current = null;
            setCallId(null);
            setRtcToken(null); // Call beendet → useCompanionRtc trennt
          }
          setAttach('searching');
        }
      } catch {
        if (!stopped) setAttach('error');
      }
    };
    void poll();
    const id = setInterval(poll, 3000);
    return () => { stopped = true; clearInterval(id); };
  }, []);

  const rtc = useCompanionRtc(rtcToken, e2ee);
  const name = getDisplayName() ?? 'Berater';

  type SaveState = { status: 'idle' | 'saving' | 'saved' | 'err' | 'pending'; folder?: string; err?: string; pending?: { name: string; blob: Blob } };
  const [scanSave, setScanSave] = useState<SaveState>({ status: 'idle' });

  // Welle 12 (pre133): Ordner-Handles vorab laden, damit Permission-Anfragen SYNCHRON in der
  // Klick-Geste angestoßen werden können — Edge verliert sonst die User-Activation über `await`.
  const handlesRef = useRef<Record<TargetKind, any | null>>({ scan: null, signed: null, nfc: null });
  const [accessNeeded, setAccessNeeded] = useState(false);

  // Zugriffsstatus der gesetzten Ordner prüfen (steuert das Freigabe-Banner).
  const refreshAccess = useCallback(async () => {
    try {
      const hs = await getHandles();
      handlesRef.current = hs;
      const st = await getAccessStates(hs);
      const need = (['scan', 'signed', 'nfc'] as TargetKind[]).some((k) => hs[k] && st[k] !== 'granted');
      setAccessNeeded(need);
    } catch { /* */ }
  }, []);

  // Auto-Ablage: still direkt schreiben, wenn die Berechtigung schon erteilt ist. Sonst NICHT
  // nach Downloads/Dialog ausweichen, sondern die Datei zur Ein-Klick-Ablage vormerken.
  const autoFile = useCallback(async (kind: TargetKind, fname: string, blob: Blob, set: (s: SaveState) => void) => {
    set({ status: 'saving' });
    try {
      const res = await saveToDir(kind, fname, blob, { interactive: false });
      if (res.ok) { set({ status: 'saved', folder: (await getDirName(kind)) ?? undefined }); return; }
      if (res.reason === 'no-dir') { set({ status: 'err', err: 'kein Ordner gesetzt' }); return; }
      set({ status: 'pending', folder: (await getDirName(kind)) ?? undefined, pending: { name: fname, blob } });
      setAccessNeeded(true);
    } catch (e) {
      set({ status: 'err', err: String(e) });
    }
  }, []);

  // Ein-Klick-Ablage (NUTZER-GESTE!): Berechtigung SYNCHRON anstoßen, dann direkt schreiben.
  const confirmFile = useCallback((kind: TargetKind, st: SaveState, set: (s: SaveState) => void) => {
    if (!st.pending) return;
    const { name: fname, blob } = st.pending;
    const grantP = requestAccessFor(handlesRef.current[kind]); // synchron in der Geste
    set({ status: 'saving', folder: st.folder, pending: st.pending });
    void (async () => {
      try {
        await grantP;
        const res = await saveToDir(kind, fname, blob, { interactive: false });
        if (res.ok) { set({ status: 'saved', folder: (await getDirName(kind)) ?? undefined }); void refreshAccess(); return; }
        const out = await saveWithDialog(fname, blob, kind);
        if (out === 'saved') set({ status: 'saved', folder: st.folder });
        else set({ status: 'pending', folder: st.folder, pending: st.pending, err: out === 'cancelled' ? undefined : out });
      } catch (e) {
        set({ status: 'pending', folder: st.folder, pending: st.pending, err: String(e) });
      }
    })();
  }, [refreshAccess]);

  // „Ordnerzugriff freigeben" — EINE Geste pro Sitzung; danach speichern NFC/Scan/Signatur still direkt.
  const grantAccess = useCallback(() => {
    requestAccessAll(handlesRef.current).then(() => void refreshAccess());
  }, [refreshAccess]);

  useEffect(() => { void refreshAccess(); }, [refreshAccess]);
  const [zoom, setZoom] = useState(1);
  const [showFolderSettings, setShowFolderSettings] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [signCount, setSignCount] = useState(0);
  const [steleLabel, setSteleLabel] = useState('');
  const [callStart, setCallStart] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentPhase, setConsentPhase] = useState<'select' | 'waiting' | 'declined'>('select');
  const sawPendingRef = useRef(false);
  // Welle 12 (pre127): Signatur-Ablage im Companion (signiertes PDF → „signed"-Ordner).
  const [sigSave, setSigSave] = useState<SaveState>({ status: 'idle' });
  const filedSigRef = useRef<string | null>(null);
  useEffect(() => {
    const sig = rtc.lastSignature;
    if (!sig || sig.status !== 'received' || !sig.url) return;
    if (filedSigRef.current === sig.sigId) return;
    filedSigRef.current = sig.sigId;
    (async () => {
      setSigSave({ status: 'saving' });
      try {
        const blob = await (await fetch(sig.url!)).blob();
        const fname = sig.filename ?? `Signiertes-Dokument-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.pdf`;
        await autoFile('signed', fname, blob, setSigSave);
      } catch (e) {
        setSigSave({ status: 'err', err: String(e) });
      }
    })();
  }, [rtc.lastSignature, autoFile]);

  // Welle 12 (pre130): EC-Karten-/NFC-Protokoll im Companion — anzeigen und als PDF ablegen.
  const [lastNfc, setLastNfc] = useState<NfcReadPayload | null>(null);
  const [nfcSave, setNfcSave] = useState<SaveState>({ status: 'idle' });
  useEffect(() => {
    function onNfc(e: Event) {
      const d = (e as CustomEvent).detail as NfcReadPayload | undefined;
      if (!d) return;
      setLastNfc(d);
      if (!d.success) { setNfcSave({ status: 'idle' }); return; }
      const fields: Record<string, string> = {};
      if (d.bancName) fields['Bank'] = d.bancName;
      if (d.blz) fields['BLZ'] = d.blz;
      if (d.bic) fields['BIC'] = d.bic;
      if (d.accountNumber) fields['Konto'] = d.accountNumber;
      if (d.pan) fields['PAN'] = d.pan;
      if (d.chipCardNumber) fields['Karten-Nr.'] = d.chipCardNumber;
      if (d.expirationDate) fields['Gültig bis'] = d.expirationDate;
      if (d.bancLocation) fields['Ort'] = d.bancLocation;
      const t = new Date();
      const pdf = buildEcProtocolPdf(fields, { callId: callId ?? 'companion', at: t.getTime() });
      const fname = `EC-Karte ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}.pdf`;
      void autoFile('nfc', fname, pdf, setNfcSave);
    }
    window.addEventListener('bamoa:nfc', onNfc);
    return () => window.removeEventListener('bamoa:nfc', onNfc);
  }, [callId, autoFile]);

  // Welle 12 (pre134): Aufzeichnungspaket-Empfang vom A/V-Gerät (P2P-DataChannel) → auf dem TS ablegen.
  type RecState = { status: 'idle' | 'receiving' | 'saving' | 'saved' | 'pending' | 'err'; pct?: number; folder?: string; err?: string; pending?: { name: string; blob: Blob } };
  const [recSave, setRecSave] = useState<RecState>({ status: 'idle' });
  const recHandleRef = useRef<any | null>(null);
  useEffect(() => { void getRecordingHandle().then((h) => { recHandleRef.current = h; }); }, []);

  // Welle 12 (pre138): Aufzeichnungsordner im Companion konfigurierbar (separates Handle).
  const [recDirName, setRecDirName] = useState<string | null>(null);
  const [recDirGranted, setRecDirGranted] = useState(false);
  const refreshRecDir = useCallback(async () => {
    setRecDirName(await getRecordingDirName());
    setRecDirGranted((await getRecordingAccess()) === 'granted');
    recHandleRef.current = await getRecordingHandle();
  }, []);
  useEffect(() => { void refreshRecDir(); }, [refreshRecDir]);
  const chooseRecDir = async () => { try { const n = await pickRecordingDir(); if (n) await refreshRecDir(); } catch { /* abgebrochen */ } };
  const removeRecDir = async () => { await clearRecordingDir(); await refreshRecDir(); };
  const confirmRecAccess = async () => { await requestRecordingAccess(recHandleRef.current); await refreshRecDir(); };
  useEffect(() => {
    function onProgress(e: Event) {
      const pct = (e as CustomEvent).detail?.pct ?? 0;
      setRecSave((s) => (s.status === 'saved' ? s : { status: 'receiving', pct }));
    }
    async function onReceived(e: Event) {
      const d = (e as CustomEvent).detail as { blob: Blob; fileName: string };
      if (!d?.blob || !d.fileName) return;
      setRecSave({ status: 'saving' });
      try {
        const r = await writeRecordingToDir(d.fileName, d.blob, { interactive: false });
        if (r === 'saved') { setRecSave({ status: 'saved', folder: (await getRecordingDirName()) ?? undefined }); return; }
        // kein Ordner / keine Berechtigung → zur Ein-Klick-Ablage vormerken (nie still Downloads)
        setRecSave({ status: 'pending', folder: (await getRecordingDirName()) ?? undefined, err: r === 'no-dir' ? 'kein Aufzeichnungsordner gesetzt' : undefined, pending: { name: d.fileName, blob: d.blob } });
      } catch (err) {
        setRecSave({ status: 'err', err: String(err) });
      }
    }
    window.addEventListener('bamoa:rec-progress', onProgress);
    window.addEventListener('bamoa:rec-received', onReceived);
    return () => { window.removeEventListener('bamoa:rec-progress', onProgress); window.removeEventListener('bamoa:rec-received', onReceived); };
  }, []);
  // Ein-Klick-Ablage der empfangenen Aufzeichnung (NUTZER-GESTE!).
  const confirmRec = useCallback((st: RecState) => {
    if (!st.pending) return;
    const { name: fname, blob } = st.pending;
    const grantP = requestRecordingAccess(recHandleRef.current); // synchron in der Geste
    setRecSave({ status: 'saving', folder: st.folder });
    void (async () => {
      try {
        await grantP;
        const r = await writeRecordingToDir(fname, blob, { interactive: false });
        if (r === 'saved') { setRecSave({ status: 'saved', folder: (await getRecordingDirName()) ?? undefined }); return; }
        const out = await saveWithDialog(fname, blob);
        if (out === 'saved') setRecSave({ status: 'saved' });
        else setRecSave({ status: 'pending', folder: st.folder, pending: st.pending, err: out === 'cancelled' ? undefined : out });
      } catch (e) {
        setRecSave({ status: 'pending', folder: st.folder, pending: st.pending, err: String(e) });
      }
    })();
  }, []);

  useEffect(() => {
    if (attach === 'attached' && callId) (window as any).__activeCallId = callId;
    else (window as any).__activeCallId = null;
  }, [attach, callId]);
  useEffect(() => { setScanSave({ status: 'idle' }); }, [rtc.lastSnapshot?.at]);
  async function fileSnapshot(forceDialog: boolean) {
    const snap = rtc.lastSnapshot;
    if (!snap) return;
    // Geste sofort nutzen: Berechtigung SYNCHRON anstoßen, bevor der Blob geholt wird
    // (sonst verfällt in Edge die User-Activation über die await-Hops).
    const grantP = forceDialog ? Promise.resolve(false) : requestAccessFor(handlesRef.current['scan']);
    setScanSave({ status: 'saving' });
    try {
      const blob = await (await fetch(snap.url)).blob();
      const t = new Date(snap.at);
      const ext = snap.format === 'jpeg' ? 'jpg' : snap.format;
      const fname = `Scan ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}.${ext}`;
      if (!forceDialog) {
        await grantP;
        const res = await saveToDir('scan', fname, blob, { interactive: false });
        if (res.ok) { setScanSave({ status: 'saved', folder: (await getDirName('scan')) ?? undefined }); void refreshAccess(); return; }
      }
      const out = await saveWithDialog(fname, blob, 'scan');
      setScanSave({ status: out === 'saved' ? 'saved' : 'idle' });
    } catch {
      setScanSave({ status: 'err', err: 'Ablage fehlgeschlagen' });
    }
  }

  const remote = useRemoteControl();
  // pre201: Stele-Label + Call-Timer für die Statusleiste.
  useEffect(() => { const inc = remote.incoming; if (inc?.steleName) setSteleLabel([inc.steleName, inc.tenantName].filter(Boolean).join(' · ')); }, [remote.incoming]);
  useEffect(() => { if (remote.avState.inCall) setCallStart((v) => v ?? Date.now()); else setCallStart(null); }, [remote.avState.inCall]);
  useEffect(() => { if (callStart == null) return; const iv = setInterval(() => setNowMs(Date.now()), 1000); return () => clearInterval(iv); }, [callStart]);
  // pre202: Consent-Modal-Phase aus dem gespiegelten A/V-Zustand ableiten.
  useEffect(() => {
    if (!consentOpen) return;
    if (remote.avState.recording) { setConsentOpen(false); setConsentPhase('select'); return; }
    if (consentPhase === 'waiting') {
      if (remote.avState.recPending) sawPendingRef.current = true;
      else if (sawPendingRef.current) setConsentPhase('declined');
    }
  }, [remote.avState.recording, remote.avState.recPending, consentOpen, consentPhase]);

  // pre140: manueller Telefonie-Status (gespiegelt via Socket 'agent:phone-status').
  const phoneBusy = useUIStore((s) => s.phoneBusy);
  const setPhoneBusyStore = useUIStore((s) => s.setPhoneBusy);
  const togglePhoneBusy = () => {
    const next = !phoneBusy;
    setPhoneBusyStore(next);      // optimistisch
    remote.setPhoneBusy(next);    // Server bestätigt via 'agent:phone-status'
  };
  // Welle 12 (pre135): Aufzeichnung aus dem Companion fern-starten (Thema wählen, A/V führt den
  // Consent-Flow auf der Stele aus). Policy für die Themenliste laden.
  const [recPolicy, setRecPolicy] = useState<RecordingPolicy>(DEFAULT_POLICY);
  const [recTopic, setRecTopic] = useState<string>('');
  useEffect(() => {
    void fetchRecordingPolicy().then((pol) => {
      setRecPolicy(pol);
      const first = enabledTopics(pol)[0];
      if (first) setRecTopic((cur) => (enabledTopics(pol).some((t) => t.id === cur) ? cur : first.id));
    });
  }, []);

  // Welle 12 (pre137): Companion-Steuerpanel ins schwebende Document-PiP abdocken.
  const pip = useCompanionPip();
  const recTopicRef = useRef('');
  recTopicRef.current = recTopic;
  const pipHandlers: CompanionPipHandlers = {
    accept: () => remote.accept(),
    decline: () => remote.decline(),
    hangup: () => remote.hangup(),
    toggleMic: () => remote.toggleMic(),
    toggleCam: () => remote.toggleCam(),
    kickGuest: () => remote.kickGuest(),
    transfer: () => useUIStore.getState().openTransfer(),
    startRecording: () => { const t = recTopicRef.current; if (t) remote.startRecording(t); },
    stopRecording: () => remote.stopRecording(),
  };
  const buildPipState = (): CompanionPipState => ({
    callId,
    incomingName: remote.incoming && !remote.avState.inCall ? (remote.incoming.steleName ?? 'Stele') : null,
    inCall: remote.avState.inCall,
    micMuted: remote.avState.micMuted,
    camOn: remote.avState.camOn,
    guestConnected: remote.avState.guestConnected,
    recording: remote.avState.recording,
    recPending: remote.avState.recPending,
    recTopicLabel: enabledTopics(recPolicy).find((t) => t.id === recTopic)?.label,
    canRecord: enabledTopics(recPolicy).length > 0,
  });
  const toggleDock = () => { if (pip.active) pip.close(); else void pip.open(); };
  // Auto-PiP best-effort: Chrome bietet 'enterpictureinpicture' nur bei Medienerfassung an
  // (z. B. während Bildschirmfreigabe). Beim Zurückkehren in den Tab schließen wir das PiP.
  useEffect(() => {
    if (!pip.supported) return;
    const openPip = () => { void pip.open(); };
    try { (navigator.mediaSession as any)?.setActionHandler?.('enterpictureinpicture', openPip); } catch { /* */ }
    const onVis = () => { if (document.visibilityState === 'visible') pip.close(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      try { (navigator.mediaSession as any)?.setActionHandler?.('enterpictureinpicture', null); } catch { /* */ }
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pip.supported]);

  const toggleShare = () => {
    if (rtc.screenSharing) void rtc.stopScreenShare();
    else void rtc.startScreenShare();
  };

  const logout = () => { clearToken(); window.location.reload(); };

  const dot = (color: string) => (
    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
  );
  const statusColor =
    rtc.status === 'connected' ? 'var(--status-available)'
    : rtc.status === 'error' ? 'var(--danger)'
    : attach === 'attached' ? 'var(--warning)'
    : 'var(--muted)';

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)', color: 'var(--fg)',
      fontFamily: 'var(--font)', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <JoinRequestPrompt />
      <TransferDrawer />
      <ChatDrawer />
      {/* Welle 12 (pre118): eingehender Anruf — identisch zur Agenten-Ansicht, fernsteuernd */}
      {remote.incoming && !remote.avState.inCall && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <IncomingCallCard
            steleName={remote.incoming.steleName ?? 'Stele'}
            tenantName={remote.incoming.tenantName ?? ''}
            group={remote.incoming.group ?? ''}
            strategy={remote.incoming.strategy}
            onAccept={() => remote.accept()}
            onDecline={() => remote.decline()}
            footerHint="Annehmen startet das Gespräch am A/V-Gerät."
            dock={{ supported: pip.supported, active: pip.active, onToggle: toggleDock }}
          />
        </div>
      )}

      <div style={{
        width: 'min(440px, 92vw)', background: 'var(--bg-elev-1)',
        border: '1px solid var(--hairline)', borderRadius: 14, padding: '26px 28px',
        boxShadow: '0 24px 80px #00000066',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', cursor: 'pointer' }}
            onClick={() => setUserMenuOpen((v) => !v)} title="Menü öffnen">
            <span
              title="Diese Session sendet kein A/V. Sie ergänzt die A/V-Session des Beraters für Dokumente und Steuerung. Beim Teilen wird dein Bildschirm dem Gast auf der Stele als Hauptbild gezeigt — Ende-zu-Ende verschlüsselt."
              style={{ fontSize: 11, letterSpacing: '.2em', textTransform: 'uppercase', color: '#c4b5fd', fontWeight: 600 }}>
              Companion
            </span>
            <span style={{ fontSize: 20, fontWeight: 500, marginTop: 2 }}>{name}</span>
            {userMenuOpen && (
              <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, minWidth: 180, background: 'var(--bg-elev-2)', border: '1px solid var(--hairline)', borderRadius: 6, boxShadow: '0 10px 30px rgba(0,0,0,0.4)', zIndex: 1000, padding: 4 }}>
                <button onClick={() => { setUserMenuOpen(false); logout(); }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  style={{ width: '100%', textAlign: 'left', padding: '8px 12px', background: 'transparent', color: 'var(--fg)', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font)' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                  Abmelden
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {pip.supported && (
              <button onClick={toggleDock} title={pip.active ? 'Angedockt (Mini-Fenster)' : 'Abdocken (Mini-Fenster)'}
                style={{ background: pip.active ? 'var(--bg-elev-3)' : 'transparent', border: '1px solid var(--hairline)', color: pip.active ? 'var(--tenant-accent)' : 'var(--muted)', width: 34, height: 34, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 7h-8v6h8V7zm2-4H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.11-.9-2-2-2zm0 16.01H3V4.98h18v14.03z" /></svg>
              </button>
            )}
          <button onClick={() => setShowFolderSettings((v) => !v)} title="Ablageordner / Einstellungen"
            style={{ background: showFolderSettings ? 'var(--bg-elev-3)' : 'transparent', border: '1px solid var(--hairline)', color: 'var(--muted)', width: 34, height: 34, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
          </button>
          </div>
        </div>

        {showFolderSettings && (
          <div onClick={() => setShowFolderSettings(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(460px, 92vw)', maxHeight: '86vh', overflowY: 'auto', background: 'var(--bg-elev-1)', border: '1px solid var(--hairline)', borderRadius: 12, padding: '20px 22px', boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <span style={{ fontSize: 15, fontWeight: 600 }}>Ablageordner</span>
                <button onClick={() => setShowFolderSettings(false)} title="Schließen" style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 0 }}>×</button>
              </div>
          <div style={{ marginTop: 10 }}><FolderTargetsConfig /></div>
          {dirPickerSupported() && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: 'rgba(124,58,237,.12)', color: 'var(--tenant-accent, #7c3aed)' }}>Aufzeichnungen</span>
              {recDirName
                ? <strong style={{ fontSize: 12.5, color: 'var(--text)' }}>{recDirName}</strong>
                : <span style={{ fontSize: 12, color: 'var(--muted)' }}>kein Ordner gesetzt</span>}
              {recDirName && (recDirGranted
                ? <span style={{ fontSize: 11.5, color: '#16a34a' }} title="Zugriff aktiv">● aktiv</span>
                : <span style={{ fontSize: 11.5, color: 'var(--warning, #d97706)' }} title="Zugriff muss bestätigt werden">● zu bestätigen</span>)}
              <span style={{ flex: 1 }} />
              <button onClick={chooseRecDir} style={miniBtn}>{recDirName ? 'Ändern' : 'Ordner wählen'}</button>
              {recDirName && !recDirGranted && (
                <button onClick={confirmRecAccess} style={{ ...miniBtn, background: 'var(--tenant-accent, #7c3aed)', color: '#fff', borderColor: 'transparent', fontWeight: 700 }}>Zugriff bestätigen</button>
              )}
              {recDirName && <button onClick={removeRecDir} style={{ ...miniBtn, color: '#DC2626' }}>Entfernen</button>}
            </div>
          )}
            </div>
          </div>
        )}

        {/* pre194: einheitliche Status-Leiste wie in der Berateransicht (verfügbar/pause/
            Nachbereitung/Besetzt) — logisch identisch über status:set / phone:busy. */}
        <div style={{ marginBottom: 16 }}>
          <StatusSwitcher />
        </div>

        {/* pre201: kompakte Statusleiste im Berater-Look — nur während eines Calls. */}
        {(remote.avState.inCall || (attach === 'attached' && !!callId)) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#141414', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 999, padding: '7px 14px', marginBottom: 4 }}>
            <svg width="20" height="14" viewBox="0 0 20 14" style={{ flexShrink: 0 }} aria-label="Verbindung zur Stele">
              {[{ x: 0, y: 9, h: 5 }, { x: 5, y: 5, h: 9 }, { x: 10, y: 1, h: 13 }].map((b, i) => {
                const lvl = rtc.channelOpen ? 3 : rtc.status === 'connected' ? 2 : 1;
                return <rect key={i} x={b.x} y={b.y} width={3.2} height={b.h} rx={0.8} fill={i < lvl ? '#34d399' : 'rgba(255,255,255,0.22)'} />;
              })}
            </svg>
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#fff', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{steleLabel || 'Stele'}</span>
            {callStart != null && (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'rgba(255,255,255,0.7)', flexShrink: 0 }}>
                {(() => { const t = Math.max(0, Math.floor((nowMs - callStart) / 1000)); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`; })()}
              </span>
            )}
            <span title="Steuerkanal (P2P) zur Stele" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: e2ee ? '#34d399' : rtc.channelOpen ? '#fbbf24' : '#fca5a5', flexShrink: 0 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              {e2ee ? 'Ende-zu-Ende' : rtc.channelOpen ? 'Verbunden' : 'Verbindet…'}
            </span>
          </div>
        )}
        {attach === 'attached' && rtc.status === 'waiting-peer' && (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, lineHeight: 1.4 }}>
            Call ist aktiv, aber die A/V-Seite ist gerade nicht im Signaling-Raum. Sobald sie
            (wieder) verbindet, koppelt der Companion automatisch an.
          </div>
        )}
        {rtc.error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 8 }}>{rtc.error}</div>}


        {/* pre197: gemeinsame Kamera-Steuerung wie in der Berateransicht (Licht/Steuerkreuz/Zoom/Scan). */}
        <CameraControls
          meshFollow={typeof localStorage !== 'undefined' && localStorage.getItem('bamoa.swap.followMesh') === '1'}
          onMeshFollow={(v) => { localStorage.setItem('bamoa.swap.followMesh', v ? '1' : '0'); }}
          available={rtc.channelOpen}
          lightOn={!!rtc.lastLight?.on}
          onToggleLight={() => rtc.setLight(!(rtc.lastLight?.on))}
          zoom={zoom}
          zoomMax={4}
          onZoom={(f) => { setZoom(f); rtc.roiZoom('main', f); }}
          onMove={(dx, dy) => rtc.roiMove('main', dx, dy)}
          onReset={() => { setZoom(1); rtc.roiReset('main'); }}
          onSnapshot={() => rtc.requestScan('main', 'jpeg')}
          scanning={rtc.scanning}
          onSwapCamera={() => remote.swap()}
        />

        {/* Scan-Ergebnis — nur wenn ein Scan vorliegt oder läuft. */}
        {(rtc.lastSnapshot || rtc.scanError || rtc.scanning) && (
        <div style={{ marginTop: 18, borderTop: '1px solid var(--hairline)', paddingTop: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>
            Scan-Ergebnis
          </div>
          {rtc.scanning && (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>
              {dot('var(--tenant-accent)')} Scan läuft …
            </div>
          )}
          {rtc.scanError && (
            <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 10 }}>{rtc.scanError}</div>
          )}
          {rtc.lastSnapshot && (
            <div style={{ marginTop: 12 }}>
              <img
                src={rtc.lastSnapshot.url}
                alt="Snapshot"
                style={{ width: '100%', borderRadius: 6, border: '1px solid var(--hairline)', display: 'block' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 8 }}>
                <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                  {rtc.lastSnapshot.role === 'main' ? 'Hauptkamera' : 'Dok-Kamera'} · {(rtc.lastSnapshot.bytes / 1024).toFixed(0)} KB
                </span>
                <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {scanSave.status === 'saved' && <span style={{ fontSize: 10.5, color: 'var(--status-available)' }}>✓ {scanSave.folder ? `„${scanSave.folder}"` : 'abgelegt'}</span>}
                  {scanSave.status === 'err' && <span style={{ fontSize: 10.5, color: '#f88' }}>{scanSave.err}</span>}
                  <button onClick={() => fileSnapshot(false)} disabled={scanSave.status === 'saving'}
                    style={{ fontSize: 11, border: 'none', borderRadius: 6, padding: '5px 10px', background: 'var(--tenant-accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: scanSave.status === 'saving' ? 0.6 : 1 }}>
                    {scanSave.status === 'saving' ? 'Speichere…' : scanSave.status === 'saved' ? 'Erneut ablegen' : 'Im Ordner ablegen'}
                  </button>
                  <button onClick={() => fileSnapshot(true)} disabled={scanSave.status === 'saving'}
                    style={{ fontSize: 11, border: '1px solid var(--hairline)', borderRadius: 6, padding: '5px 10px', background: 'transparent', color: 'var(--text)', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Speichern unter…
                  </button>
                </span>
              </div>
            </div>
          )}
        </div>
        )}

        {accessNeeded && (
          <div style={{ marginTop: 16, padding: '10px 12px', borderRadius: 8, background: 'rgba(245,158,11,.12)', border: '1px solid rgba(245,158,11,.4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.4 }}>
              Damit Scans, EC-Protokolle und Unterschriften <b>direkt</b> im Ordner landen, einmal pro Sitzung den Ordnerzugriff freigeben.
            </span>
            <button onClick={grantAccess} style={{ flexShrink: 0, fontSize: 11.5, border: 'none', borderRadius: 6, padding: '6px 12px', background: 'var(--tenant-accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Ordnerzugriff freigeben
            </button>
          </div>
        )}

        {/* Unterschrift — nur wenn ein Dokument vorliegt oder eine Signatur läuft. */}
        {(signCount > 0 || rtc.lastSignature) && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--hairline)' }}>
          <div style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 600, marginBottom: 8 }}>Unterschrift</div>
          <SignInbox onSign={(f) => rtc.submitSignature(f)} canSign={rtc.channelOpen} onCount={setSignCount} />
          {rtc.lastSignature && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
              {rtc.lastSignature.status === 'uploading' && `Übertrage Dokument zur Stele… ${rtc.lastSignature.progress ?? 0}%`}
              {rtc.lastSignature.status === 'submitted' && 'Auf der Stele — wartet auf Unterschrift…'}
              {rtc.lastSignature.status === 'signing' && `Signiere / empfange Ergebnis… ${rtc.lastSignature.progress ?? 0}%`}
              {rtc.lastSignature.status === 'received' && (sigSave.status === 'saved'
                ? `Signiert und gespeichert${sigSave.folder ? ` (${sigSave.folder})` : ''}.`
                : sigSave.status === 'saving' ? 'Signiert — speichere…'
                : sigSave.status === 'pending' ? `Signiert${sigSave.folder ? ` — bereit für „${sigSave.folder}"` : ''}.`
                : sigSave.status === 'err' ? `Signiert, aber Speichern fehlgeschlagen: ${sigSave.err}`
                : 'Signiert.')}
              {rtc.lastSignature.status === 'aborted' && 'Unterschrift abgebrochen.'}
              {rtc.lastSignature.status === 'error' && `Fehler: ${rtc.lastSignature.error ?? 'unbekannt'}`}
            </div>
          )}
          {rtc.lastSignature?.status === 'received' && rtc.lastSignature.url && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {sigSave.status === 'pending' && sigSave.pending && (
                <button onClick={() => confirmFile('signed', sigSave, setSigSave)}
                  style={{ fontSize: 11.5, border: 'none', borderRadius: 6, padding: '6px 12px', background: 'var(--tenant-accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {sigSave.folder ? `Jetzt in „${sigSave.folder}" speichern` : 'Jetzt im Ordner speichern'}
                </button>
              )}
              <button onClick={() => window.open(rtc.lastSignature!.url!, '_blank')} style={barBtn('normal')}>Öffnen</button>
              <button
                onClick={() => { const u = rtc.lastSignature!.url!; const fn = rtc.lastSignature!.filename ?? 'Signiertes-Dokument.pdf'; void (async () => { setSigSave({ status: 'saving' }); try { const blob = await (await fetch(u)).blob(); const out = await saveWithDialog(fn, blob, 'signed'); setSigSave(out === 'saved' ? { status: 'saved' } : { status: 'err', err: out }); } catch (e) { setSigSave({ status: 'err', err: String(e) }); } })(); }}
                style={barBtn('normal')}
              >Speichern unter…</button>
            </div>
          )}
        </div>
        )}

        {/* Welle 12 (pre130): EC-Karte / NFC-Protokoll */}
        {lastNfc && (
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--hairline)' }}>
            <div style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 600, marginBottom: 8 }}>EC-Karte</div>
            {lastNfc.success ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 12px', fontSize: 12.5 }}>
                  {lastNfc.bancName && (<><span style={{ color: 'var(--muted)' }}>Bank</span><span>{lastNfc.bancName}</span></>)}
                  {lastNfc.blz && (<><span style={{ color: 'var(--muted)' }}>BLZ</span><span>{lastNfc.blz}</span></>)}
                  {lastNfc.bic && (<><span style={{ color: 'var(--muted)' }}>BIC</span><span>{lastNfc.bic}</span></>)}
                  {lastNfc.accountNumber && (<><span style={{ color: 'var(--muted)' }}>Konto</span><span>{lastNfc.accountNumber}</span></>)}
                  {lastNfc.pan && (<><span style={{ color: 'var(--muted)' }}>PAN</span><span>{lastNfc.pan}</span></>)}
                  {lastNfc.expirationDate && (<><span style={{ color: 'var(--muted)' }}>Gültig</span><span>{lastNfc.expirationDate}</span></>)}
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
                  {nfcSave.status === 'saving' && 'Protokoll wird abgelegt…'}
                  {nfcSave.status === 'saved' && `Protokoll gespeichert${nfcSave.folder ? ` (${nfcSave.folder})` : ''}.`}
                  {nfcSave.status === 'pending' && `Protokoll bereit${nfcSave.folder ? ` für „${nfcSave.folder}"` : ''} — bitte ablegen.`}
                  {nfcSave.status === 'err' && `Protokoll-Ablage fehlgeschlagen: ${nfcSave.err}`}
                </div>
                {nfcSave.status === 'pending' && nfcSave.pending && (
                  <button onClick={() => confirmFile('nfc', nfcSave, setNfcSave)}
                    style={{ marginTop: 8, fontSize: 11.5, border: 'none', borderRadius: 6, padding: '6px 12px', background: 'var(--tenant-accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {nfcSave.folder ? `Jetzt in „${nfcSave.folder}" speichern` : 'Jetzt im Ordner speichern'}
                  </button>
                )}
              </>
            ) : (
              <div style={{ fontSize: 12.5, color: '#fca5a5' }}>Karte konnte nicht gelesen werden.</div>
            )}
          </div>
        )}

        {/* Welle 12 (pre134): Empfang des Aufzeichnungspakets vom A/V-Gerät (P2P) → Ablage auf dem TS. */}
        {recSave.status !== 'idle' && (
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--hairline)' }}>
            <div style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 600, marginBottom: 8 }}>Aufzeichnung</div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
              {recSave.status === 'receiving' && `Aufzeichnung wird übertragen… ${recSave.pct ?? 0}%`}
              {recSave.status === 'saving' && 'Aufzeichnung wird abgelegt…'}
              {recSave.status === 'saved' && `Aufzeichnung gespeichert${recSave.folder ? ` (${recSave.folder})` : ''}.`}
              {recSave.status === 'pending' && `Aufzeichnung empfangen${recSave.folder ? ` — bereit für „${recSave.folder}"` : ''}${recSave.err ? ` (${recSave.err})` : ''} — bitte ablegen.`}
              {recSave.status === 'err' && `Aufzeichnung-Ablage fehlgeschlagen: ${recSave.err}`}
            </div>
            {recSave.status === 'pending' && recSave.pending && (
              <button onClick={() => confirmRec(recSave)}
                style={{ marginTop: 8, fontSize: 11.5, border: 'none', borderRadius: 6, padding: '6px 12px', background: 'var(--tenant-accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                {recSave.folder ? `Jetzt in „${recSave.folder}" speichern` : 'Jetzt im Ordner speichern'}
              </button>
            )}
          </div>
        )}

        {/* Roster (Gruppen/Kollegen) — Chat über die Roster-Icons. */}
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--hairline)' }}>
          <Roster remote />
        </div>

        {/* pre200: Anrufleiste am unteren Rand der Karte. */}
        {/* Welle 12 (pre139): Steuerleiste über gemeinsame Komponente (CallControlBar). */}
        {(remote.avState.inCall || (attach === 'attached' && !!callId)) && (
          <CallControlBar
            micMuted={!!remote.avState.micMuted}
            camOn={!!remote.avState.camOn}
            guestConnected={!!remote.avState.guestConnected}
            recording={!!remote.avState.recording}
            recPending={!!remote.avState.recPending}
            topics={enabledTopics(recPolicy)}
            recTopic={recTopic}
            onSelectTopic={setRecTopic}
            onToggleMic={() => remote.toggleMic()}
            onToggleCam={() => remote.toggleCam()}
            onKickGuest={() => remote.kickGuest()}
            onTransfer={() => useUIStore.getState().openTransfer()}
            onHangup={() => remote.hangup()}
            onStartRecording={() => { setConsentPhase('select'); setConsentOpen(true); }}
            onStopRecording={() => remote.stopRecording()}
            footer="Steuert das A/V-Gerät · Status wird gespiegelt"
            screenSharing={rtc.screenSharing}
            onToggleShare={toggleShare}
            shareAvailable={rtc.status === 'connected'}
          />
        )}

        <RecordingConsentModal
          open={consentOpen}
          phase={consentPhase}
          topic={recTopic}
          topics={enabledTopics(recPolicy)}
          onSelectTopic={setRecTopic}
          onRequestConsent={() => { if (recTopic) { remote.startRecording(recTopic); sawPendingRef.current = false; setConsentPhase('waiting'); } }}
          onCancel={() => { setConsentOpen(false); setConsentPhase('select'); }}
          onCancelWaiting={() => { remote.stopRecording(); setConsentOpen(false); setConsentPhase('select'); }}
          onClose={() => { setConsentOpen(false); setConsentPhase('select'); }}
        />

      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
      <span style={{ color: 'var(--muted)', fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
      <span>{children}</span>
    </div>
  );
}

const ctrlLabel: React.CSSProperties = { fontSize: 10, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 };
function ctrlBtn(bg?: string, disabled?: boolean): React.CSSProperties {
  return {
    padding: '7px 12px', borderRadius: 8, border: '1px solid var(--hairline)', fontFamily: 'inherit',
    background: disabled ? 'var(--bg-elev-3)' : (bg ?? 'var(--bg-elev-2)'),
    color: disabled ? 'var(--muted)' : (bg ? '#fff' : 'var(--text)'),
    fontSize: 12.5, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

const miniBtn: React.CSSProperties = {
  fontSize: 11.5, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--hairline, #e3e6ea)',
  background: 'var(--surface, #fff)', color: 'var(--text, #15181d)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
};

